import React, { useState } from 'react';
import { MapPin, Calendar, Users, CheckCircle, AlertCircle, Ticket, Globe, Camera, Sunrise, Waves } from 'lucide-react';

const SriLankaBookingComponent = () => {
  const [bookingData] = useState({
    customerName: "Charith",
    destinations: [
      {
        name: "Sri Lanka Cultural Triangle Pass - 5 Day Explorer",
        type: "destination",
        validFrom: "Monday 20 January 2025",
        validUntil: "Friday 24 January 2025",
        tickets: "2 Adults",
        includes: [
          "Access to 5 UNESCO World Heritage Sites",
          "Sigiriya Rock Fortress entry",
          "Dambulla Cave Temple access",
          "Ancient city of Polonnaruwa",
          "Temple of the Tooth guided tour"
        ],
        price: "LKR 45,000.00"
      },
      {
        name: "South Coast Beach & Wildlife Pass - 3 Day",
        type: "destination",
        validFrom: "Saturday 25 January 2025",
        validUntil: "Monday 27 January 2025",
        tickets: "2 Adults",
        includes: [
          "Yala National Park safari entry",
          "Mirissa whale watching tour",
          "Galle Fort walking tour",
          "Beach access passes",
          "Transportation included"
        ],
        price: "LKR 38,000.00"
      }
    ],
    activities: [
      {
        name: "Sigiriya Rock Fortress Sunrise Climb",
        date: "Tuesday 21 January 2025",
        time: "05:30 - 09:00",
        tickets: "2 Adults",
        location: "Sigiriya, Central Province",
        price: "LKR 12,500.00",
        icon: "sunrise"
      },
      {
        name: "Kandy Esala Perahera Cultural Show",
        date: "Wednesday 22 January 2025",
        time: "19:00 - 21:00",
        tickets: "2 Adults",
        location: "Temple of the Tooth, Kandy",
        price: "LKR 8,000.00",
        icon: "camera"
      },
      {
        name: "Ella to Kandy Scenic Train Journey",
        date: "Thursday 23 January 2025",
        time: "09:00 - 16:00",
        tickets: "2 Adults - First Class Reserved",
        location: "Ella Railway Station to Kandy",
        price: "LKR 6,500.00",
        icon: "camera"
      },
      {
        name: "Yala National Park Leopard Safari",
        date: "Saturday 25 January 2025",
        time: "05:30 - 11:00",
        tickets: "2 Adults",
        location: "Yala National Park, Hambantota",
        price: "LKR 18,000.00",
        icon: "camera"
      },
      {
        name: "Mirissa Whale & Dolphin Watching Tour",
        date: "Sunday 26 January 2025",
        time: "06:00 - 12:00",
        tickets: "2 Adults",
        location: "Mirissa Harbor, Southern Province",
        price: "LKR 15,000.00",
        icon: "waves"
      },
      {
        name: "Galle Fort Sunset Heritage Walk",
        date: "Sunday 26 January 2025",
        time: "17:00 - 19:30",
        tickets: "2 Adults",
        location: "Galle Fort, Galle",
        price: "LKR 4,500.00",
        icon: "camera"
      }
    ],
    pricing: {
      destinations: 83000.00,
      activities: 64500.00,
      total: 147500.00
    },
    paymentDate: "15 Jan 2025",
    cardLast4: "4223",
    bookingReference: "SLCEY2025-7842"
  });

  const getActivityIcon = (iconType) => {
    switch(iconType) {
      case 'sunrise': return <Sunrise className="text-orange-500" size={16} />;
      case 'waves': return <Waves className="text-blue-500" size={16} />;
      default: return <Camera className="text-purple-500" size={16} />;
    }
  };

  return (
    <div className="max-w-2xl mx-auto bg-gray-50 p-4 font-sans">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-4">
        <div className="flex items-start gap-3 mb-4">
          <CheckCircle className="text-green-600 flex-shrink-0" size={24} />
          <div>
            <h1 className="text-xl font-bold text-gray-900">Ayubowan {bookingData.customerName}!</h1>
            <p className="text-gray-700 mt-1">Your Sri Lanka adventure is confirmed.</p>
          </div>
        </div>
        
        <p className="text-sm text-gray-600 mb-4">
          All your destination passes and activity tickets have been confirmed. You'll receive separate emails with vouchers and meeting point details for each experience. For urgent assistance, contact our Sri Lanka travel desk at <span className="font-semibold">+94 77 123 4567</span>.
        </p>
        
        <p className="text-xs text-gray-500">
          Booking Reference: {bookingData.bookingReference}
        </p>
      </div>

      {/* Safety Notice */}
      <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-4 rounded">
        <div className="flex items-start gap-3">
          <AlertCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <h3 className="font-semibold text-gray-900 text-sm">Stay safe while traveling</h3>
            <p className="text-sm text-gray-700 mt-1">
              Never share your booking confirmation or payment details with third parties. All official communications will come from our verified email addresses only.
            </p>
            <button className="text-blue-600 text-sm mt-2 hover:underline">Travel safety tips</button>
          </div>
        </div>
      </div>

      {/* Destination Passes Section */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-4">
        <div className="flex items-center gap-2 mb-4">
          <Globe className="text-green-600" size={24} />
          <h2 className="text-lg font-bold text-gray-900">Destination Passes</h2>
        </div>

        {bookingData.destinations.map((destination, index) => (
          <div key={index} className={`pb-4 ${index < bookingData.destinations.length - 1 ? 'border-b border-gray-200 mb-4' : ''}`}>
            <div className="flex items-start justify-between mb-2">
              <h3 className="font-semibold text-gray-900 text-sm flex-1">{destination.name}</h3>
              <span className="bg-green-100 text-green-800 text-xs font-semibold px-2 py-1 rounded ml-2">Multi-Day Pass</span>
            </div>
            
            <div className="space-y-2 text-sm mb-3">
              <p className="text-gray-600">
                <Calendar className="inline mr-1" size={14} />
                Valid from: {destination.validFrom}
              </p>
              <p className="text-gray-600">
                <Calendar className="inline mr-1" size={14} />
                Valid until: {destination.validUntil}
              </p>
              <p className="text-gray-600">
                <Users className="inline mr-1" size={14} />
                {destination.tickets}
              </p>
            </div>

            <div className="bg-green-50 rounded p-3 mb-3">
              <p className="text-xs font-semibold text-gray-700 mb-2">What's included:</p>
              <ul className="text-xs text-gray-600 space-y-1">
                {destination.includes.map((item, idx) => (
                  <li key={idx} className="flex items-start">
                    <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-0.5" size={12} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <p className="text-gray-900 font-semibold">{destination.price}</p>
          </div>
        ))}
      </div>

      {/* Activities Section */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-4">
        <div className="flex items-center gap-2 mb-4">
          <Ticket className="text-purple-600" size={24} />
          <h2 className="text-lg font-bold text-gray-900">Activities & Experiences</h2>
        </div>

        {bookingData.activities.map((activity, index) => (
          <div key={index} className={`pb-4 ${index < bookingData.activities.length - 1 ? 'border-b border-gray-200 mb-4' : ''}`}>
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-start gap-2 flex-1">
                {getActivityIcon(activity.icon)}
                <h3 className="font-semibold text-gray-900 text-sm flex-1">{activity.name}</h3>
              </div>
              <span className="bg-purple-100 text-purple-800 text-xs font-semibold px-2 py-1 rounded ml-2">Activity</span>
            </div>
            
            <div className="space-y-2 text-sm">
              <p className="text-gray-600">
                <Calendar className="inline mr-1" size={14} />
                {activity.date}
              </p>
              <p className="text-gray-600">
                <span className="font-medium">Time:</span> {activity.time}
              </p>
              <p className="text-gray-600">
                <Users className="inline mr-1" size={14} />
                {activity.tickets}
              </p>
              <p className="text-gray-600">
                <MapPin className="inline mr-1" size={14} />
                {activity.location}
              </p>
              <p className="text-gray-900 font-semibold">{activity.price}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Pricing Summary */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-4">
        <h2 className="text-lg font-bold text-gray-900 mb-4">Total price</h2>
        
        <div className="space-y-2 text-sm mb-4">
          <div className="flex justify-between">
            <span className="text-gray-600">Destination Passes</span>
            <span className="text-gray-900">LKR {bookingData.pricing.destinations.toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Activities & Experiences</span>
            <span className="text-gray-900">LKR {bookingData.pricing.activities.toFixed(2)}</span>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-3">
          <div className="flex justify-between items-center">
            <span className="font-bold text-gray-900">Total price</span>
            <span className="font-bold text-xl text-gray-900">LKR {bookingData.pricing.total.toFixed(2)}</span>
          </div>
        </div>

        <p className="text-xs text-gray-500 mt-3">
          All taxes and service charges included. Prices shown in Sri Lankan Rupees (LKR).
        </p>
      </div>

      {/* Payment Information */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-3">Payment information</h2>
        <p className="text-sm text-gray-700 mb-4">
          You've scheduled a payment for this booking, and we'll charge your card automatically on:
        </p>
        
        <div className="bg-gray-50 rounded p-4 mb-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-semibold text-gray-900">{bookingData.paymentDate}</span>
          </div>
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <div className="bg-blue-600 text-white px-2 py-1 rounded text-xs font-bold">VISA</div>
              <span className="text-sm text-gray-600">•••• {bookingData.cardLast4}</span>
            </div>
            <span className="text-sm font-semibold text-gray-900">LKR {bookingData.pricing.total.toFixed(2)}</span>
          </div>
        </div>

        <div className="flex gap-3">
          <button className="flex-1 bg-blue-600 text-white py-3 px-4 rounded font-semibold hover:bg-blue-700 transition-colors">
            Pay now
          </button>
          <button className="flex-1 border-2 border-blue-600 text-blue-600 py-3 px-4 rounded font-semibold hover:bg-blue-50 transition-colors">
            Update payment method
          </button>
        </div>
      </div>

      {/* Modify Booking Button */}
      <div className="mt-4">
        <button className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors">
          Modify your booking
        </button>
      </div>
    </div>
  );
};

export default SriLankaBookingComponent;